// ACTIONS: change an open reminder's text or time (`PATCH crews/me/reminders/{id}`). One that
// fired or was dismissed is not edited (the API answers 409): set a new one instead.
import type { OperationOutput } from "@companyos/sdk";
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldLabel,
  Input,
  toast,
} from "@companyos/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PencilIcon } from "lucide-react";
import { type FormEvent, useId, useState } from "react";
import type { Api } from "../../api/client";
import { crewKeys } from "../queries";
import { problemText } from "../settings/problem-text";
import { RowAction } from "../settings/row-actions";

type Reminder = OperationOutput<"crews.addReminder">;

/** An instant as a `datetime-local` value in the viewer's zone, to the minute. */
function localValue(iso: string): string {
  const t = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${t.getFullYear()}-${pad(t.getMonth() + 1)}-${pad(t.getDate())}T${pad(t.getHours())}:${pad(t.getMinutes())}`;
}

export function ReminderEditAction({
  api,
  co,
  reminder: r,
}: {
  api: Api;
  co: string;
  reminder: Reminder;
}) {
  const [open, setOpen] = useState(false);
  if (r.fired_at || r.dismissed_at) return null;
  return (
    <>
      <RowAction
        icon={PencilIcon}
        label={`Edit ${r.text}`}
        tip="Edit"
        onClick={() => setOpen(true)}
      />
      {open ? <EditDialog api={api} co={co} reminder={r} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

function EditDialog({
  api,
  co,
  reminder: r,
  onClose,
}: {
  api: Api;
  co: string;
  reminder: Reminder;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const ids = { text: useId(), when: useId() };
  const [text, setText] = useState(r.text);
  const [when, setWhen] = useState(localValue(r.at));
  const at = when ? new Date(when) : null;
  const valid = Boolean(text.trim()) && at !== null && !Number.isNaN(at.getTime());
  const save = useMutation({
    mutationFn: () =>
      api.client.crews.updateReminder({
        params: { co, id: r.id },
        body: {
          text: text.trim(),
          ...(at && when !== localValue(r.at) ? { at: at.toISOString() } : {}),
        },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: crewKeys.reminders(co) });
      toast.add({ title: "Reminder saved", type: "success" });
      onClose();
    },
  });
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (valid) save.mutate();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !save.isPending && onClose()}>
      <DialogContent>
        <form onSubmit={onSubmit} noValidate className="flex flex-col gap-5">
          <DialogHeader>
            <DialogTitle>Edit reminder</DialogTitle>
            <DialogDescription>It lands in your Inbox at the new time.</DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor={ids.text}>Reminder</FieldLabel>
            <Input
              id={ids.text}
              value={text}
              maxLength={500}
              onChange={(e) => setText(e.target.value)}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor={ids.when}>When</FieldLabel>
            <Input
              id={ids.when}
              type="datetime-local"
              value={when}
              onChange={(e) => setWhen(e.target.value)}
            />
          </Field>
          {save.isError ? (
            <p role="alert" className="text-xs text-danger-text">
              {problemText(save.error, "The reminder was not saved.")}
            </p>
          ) : null}
          <DialogFooter>
            <DialogClose render={<Button variant="ghost" type="button" />}>Cancel</DialogClose>
            <Button type="submit" disabled={!valid || save.isPending}>
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
